import { ReactNode } from "react";
import { LEGAL_ENTITY } from "@/config/legal";
import { A, ContactEmail, LegalDocument, LegalSection, LegalTable, List, P } from "./LegalLayout";

/**
 * Cookie Policy, shared by the marketing site and every business's website
 * (same app, same storage). Every key below was found in the code on
 * 2 November 2026 — if you add browser storage or a third-party embed, add it here.
 */

const TOC = [
  { id: "what-are-cookies", heading: "Cookies and similar technologies" },
  { id: "what-we-store", heading: "What we store in your browser" },
  { id: "third-parties", heading: "Third-party services" },
  { id: "consent", heading: "Why there’s no cookie banner" },
  { id: "control", heading: "How to control cookies and storage" },
  { id: "changes", heading: "Changes to this policy" },
  { id: "contact", heading: "Contact" },
];

type StorageType = "Local storage" | "Session storage";

const code = (text: string) => <code className="rounded [overflow-wrap:anywhere] bg-muted px-1 py-0.5 text-xs text-foreground">{text}</code>;

const STORAGE_ROWS: [ReactNode, StorageType, string, string, string][] = [
  [
    code("sb-…-auth-token"),
    "Local storage",
    "Keeps you signed in to the admin or to your customer account, and keeps your sign-in secure. Other keys beginning “sb-” may be set during sign-in for the same reason.",
    "Until you sign out",
    "Strictly necessary",
  ],
  [
    code("admin.selectedOrganizationId"),
    "Local storage",
    "Remembers which business you’re managing, if your login belongs to more than one.",
    "Until you sign out",
    "Strictly necessary",
  ],
  [
    code("tpv:booking-payment:…"),
    "Session storage",
    "Remembers your booking while you pay on Stripe’s page, so we can show the right confirmation when you come back.",
    "Cleared after use, or when you close the tab",
    "Strictly necessary",
  ],
  [
    code("signup.pendingConfirmationEmail"),
    "Local storage",
    "Remembers the email address you signed up with, so you can resend the confirmation email.",
    "Removed once your email is confirmed",
    "Strictly necessary",
  ],
  [
    code("adm-bell-seen:…"),
    "Local storage",
    "Remembers which admin notifications you’ve already seen.",
    "Until you clear your browser storage",
    "Functional — remembers your choices",
  ],
  [
    code("cleaner-notifications-viewed:…"),
    "Local storage",
    "Remembers which job notifications a team member has already seen.",
    "Until you clear your browser storage",
    "Functional — remembers your choices",
  ],
  [
    code("gs-checklist-dismissed-…"),
    "Local storage",
    "Remembers that you hid the getting-started checklist in the admin.",
    "Until you clear your browser storage",
    "Functional — remembers your choices",
  ],
  [
    <>
      {code("__app_reload_count")}, {code("__app_reload_last_at")}
    </>,
    "Session storage",
    "Only set when you open a troubleshooting link we send you. Counts page reloads to help us fix a fault.",
    "When you close the tab",
    "Strictly necessary",
  ],
  [
    <>
      {code("tpv-smoke-checklist-v1")}, {code("tpv-uat-checklist-v1")}
    </>,
    "Local storage",
    "Used only by our own staff, to save progress on internal testing checklists.",
    "Until cleared",
    "Strictly necessary",
  ],
];

/** Name and storage type share the first column so the table fits the page width. */
const STORAGE_TABLE_ROWS = STORAGE_ROWS.map(([name, type, purpose, howLong, category]) => [
  <>
    {name}
    <span className="mt-1 block text-xs">{type}</span>
  </>,
  purpose,
  howLong,
  category,
]);

export function CookiePolicyContent({ businessName }: { businessName?: string }) {
  const isTenant = Boolean(businessName);
  const siteName = isTenant ? `${businessName}’s website` : "thepolishedversion.com";

  return (
    <LegalDocument
      slug="cookies"
      title="Cookie Policy"
      intro={
        isTenant
          ? `This policy explains the cookies and similar technologies used on ${businessName}’s website, which is provided by The Polished Version.`
          : "This policy explains the cookies and similar technologies used on thepolishedversion.com and on the cleaning business websites we provide."
      }
      toc={TOC}
      summary={
        <List
          items={[
            "We don’t use analytics, advertising or tracking cookies.",
            "We don’t set any cookies of our own. We use your browser’s local storage and session storage, but only to keep you signed in, complete bookings and remember choices you make.",
            "Some pages load content from other companies, such as Google Maps or an embedded video, and those companies may set their own cookies. These are explained below.",
          ]}
        />
      }
    >
      <LegalSection id="what-are-cookies" heading="Cookies and similar technologies">
        <P>
          Cookies are small files a website saves in your browser. Local storage and session storage do a similar job: session
          storage is cleared when you close the tab, and local storage stays until it is removed. UK law (the Privacy and
          Electronic Communications Regulations, or PECR) treats all of these in the same way, so this policy covers them all.
        </P>
        {isTenant && (
          <P>
            {businessName}’s website runs on The Polished Version, which sets the storage described below on {businessName}’s
            behalf.
          </P>
        )}
      </LegalSection>

      <LegalSection id="what-we-store" heading="What we store in your browser">
        <P>
          This is everything {siteName} stores in your browser. Most visitors will only ever have the first one or two, and only
          if they sign in or pay online. The rest are used only in the admin by signed-in business owners and team members. In the
          names below, “…” stands for an ID that differs per person, booking or business.
        </P>
        <LegalTable
          caption="Browser storage used by the website"
          head={["Name and type", "What it’s for", "How long", "Category"]}
          rows={STORAGE_TABLE_ROWS}
          columnClassNames={["min-w-[10rem]", "min-w-[13rem]", "min-w-[7rem]", "min-w-[7rem]"]}
        />
      </LegalSection>

      <LegalSection id="third-parties" heading="Third-party services">
        <P>Some features rely on other companies. They control their own cookies, under their own policies:</P>
        <List
          items={[
            <>
              <strong className="font-semibold text-foreground">Stripe</strong>: when you pay by card, or a business manages its
              subscription, you are taken to Stripe’s secure pages on stripe.com. Stripe sets cookies there to make payments work
              and prevent fraud. See <A href="https://stripe.com/legal/cookies-policy">Stripe’s cookie policy</A>.
            </>,
            <>
              <strong className="font-semibold text-foreground">PayPal</strong>: if you follow a business’s PayPal.me link, you
              leave our site and PayPal’s own cookie policy applies.
            </>,
            <>
              <strong className="font-semibold text-foreground">Google Maps</strong>: a business’s Contact page may show a map of
              its service area, loaded from Google. Google may set or read cookies when the map loads. See{" "}
              <A href="https://policies.google.com/technologies/cookies">how Google uses cookies</A>.
            </>,
            <>
              <strong className="font-semibold text-foreground">YouTube and Vimeo</strong>: if a business adds a video to its
              website, it is embedded using YouTube’s privacy-enhanced mode or Vimeo’s “do not track” setting, which limit the
              cookies they set. The video provider may store information in your browser once you play the video.
            </>,
            <>
              <strong className="font-semibold text-foreground">Google Fonts and Google reviews</strong>: website fonts, and the
              photos of people who wrote Google reviews, are loaded from Google’s servers. These don’t set cookies, but Google
              receives your IP address, as with any website your browser contacts.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection id="consent" heading="Why there’s no cookie banner">
        <P>
          Under PECR, websites don’t need your consent for storage that is strictly necessary to provide a service you have asked
          for, such as keeping you signed in or completing a booking. Remembering a choice you have made, such as hiding a
          checklist, falls within the same exemption. All of the storage we set falls into these groups, and we don’t use any
          analytics or advertising cookies, so we don’t ask for cookie consent.
        </P>
        <P>
          The third-party services above set their own cookies under their own policies. You can block them using your browser
          settings without affecting the rest of the website, although a map or video may then not display.
        </P>
      </LegalSection>

      <LegalSection id="control" heading="How to control cookies and storage">
        <P>
          You can view, block and delete cookies and site data in your browser settings; your browser’s help pages explain how.
          Most browsers also let you block third-party cookies only. If you clear site data for this website you will be signed
          out, and the website will forget choices such as which notifications you’ve seen. Blocking storage completely may stop
          sign-in and online payment from working.
        </P>
      </LegalSection>

      <LegalSection id="changes" heading="Changes to this policy">
        <P>
          If we start using new cookies or storage, we will update this policy and the “Last updated” date. If we ever want to
          use storage that isn’t strictly necessary, such as analytics, we will ask for your consent first.
        </P>
      </LegalSection>

      <LegalSection id="contact" heading="Contact">
        {isTenant ? (
          <P>
            For questions about how {businessName} uses your information, see its <A href="/privacy">Privacy Policy</A> or{" "}
            <A href="/contact">contact {businessName}</A>. For questions about this website’s technology, email The Polished
            Version at <ContactEmail />.
          </P>
        ) : (
          <P>
            Questions about this policy? Email <ContactEmail />. Our <A href="/privacy">Privacy Policy</A> explains how we use
            personal information.
          </P>
        )}
        {isTenant && (
          <P>
            This website is provided by <A href={LEGAL_ENTITY.websiteUrl}>The Polished Version</A>.
          </P>
        )}
      </LegalSection>
    </LegalDocument>
  );
}
